import { ChevronUp, ChevronDown, Download } from "lucide-react";
import Logo from "./Logo";

export const TopBar = ({ active, goTo, slides }) => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-bone/80 backdrop-blur-md">
    <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between px-6 md:px-16 lg:px-24">
      <button
        data-testid="topbar-logo"
        onClick={() => goTo(0)}
        aria-label="PranIQ — back to first slide"
        className="transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
      >
        <Logo />
      </button>
      <div
        data-testid="topbar-breadcrumb"
        className="hidden font-mono text-[11px] uppercase tracking-[0.22em] text-mut md:block"
      >
        <span className="text-navy">{String(active + 1).padStart(2, "0")}</span>
        {" / "}
        {String(slides.length).padStart(2, "0")} — {slides[active].label}
      </div>
      <div className="flex items-center gap-3">
        <a
          data-testid="topbar-deck-btn"
          href="/deck"
          className="hidden items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-medium text-ink transition-colors duration-200 hover:border-ink/50 sm:inline-flex"
        >
          <Download size={15} />
          Download PDF
        </a>
        <button
          data-testid="topbar-cta"
          onClick={() => goTo(slides.length - 1)}
          className="rounded-full bg-ink px-5 py-2.5 text-sm font-medium text-bone transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
        >
          Request a call back
        </button>
      </div>
    </div>
  </header>
);

export const Rail = ({ active, goTo, slides }) => (
  <nav
    data-testid="progress-rail"
    className="fixed right-5 top-1/2 z-50 hidden -translate-y-1/2 flex-col items-center gap-2.5 rounded-full border border-line/70 bg-bone/70 px-2.5 py-4 backdrop-blur-md lg:flex"
  >
    {slides.map((s, i) => (
      <button
        key={s.id}
        data-testid={`rail-dot-${String(i + 1).padStart(2, "0")}`}
        onClick={() => goTo(i)}
        aria-label={`Go to slide ${i + 1}: ${s.label}`}
        className="group relative flex h-3 w-3 items-center justify-center"
      >
        <span
          className={`block rounded-full transition-all duration-300 ${
            i === active
              ? "h-5 w-1.5 bg-gold"
              : "h-1.5 w-1.5 bg-ink/25 group-hover:bg-ink/50"
          }`}
        />
        <span className="pointer-events-none absolute right-6 whitespace-nowrap rounded-full border border-line bg-bone px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          {String(i + 1).padStart(2, "0")} · {s.label}
        </span>
      </button>
    ))}
  </nav>
);

export const DeckControls = ({ active, goTo, slides }) => (
  <div
    data-testid="deck-controls"
    className="fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-ink/90 py-2 pr-2 pl-2 text-bone shadow-xl backdrop-blur"
  >
    <button
      data-testid="deck-prev-btn"
      onClick={() => goTo(active - 1)}
      disabled={active === 0}
      aria-label="Previous slide"
      className="rounded-full p-2 transition hover:bg-white/10 disabled:opacity-30"
    >
      <ChevronUp size={16} />
    </button>
    <span
      data-testid="deck-counter"
      className="px-3 font-mono text-[11px] tracking-[0.2em]"
    >
      {String(active + 1).padStart(2, "0")} /{" "}
      {String(slides.length).padStart(2, "0")}
    </span>
    <button
      data-testid="deck-next-btn"
      onClick={() => goTo(active + 1)}
      disabled={active === slides.length - 1}
      aria-label="Next slide"
      className="rounded-full p-2 transition hover:bg-white/10 disabled:opacity-30"
    >
      <ChevronDown size={16} />
    </button>
  </div>
);
